import React, { useState } from 'react';
import { OWNER_PICKER, PHASE_PICKER, type StepDraft, type StepOwner, type StepPhase } from '../../utils/projectSteps';
import { BUTTON_GHOST, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';

/**
 * Add or edit one tracker step. Split out of ProjectSteps.tsx on 14 Oct 2026,
 * when the list gained drag-to-reorder: the combined file was close to the
 * size this editor silently truncates on write, and a file that cannot be
 * safely written is a file that cannot be safely fixed.
 *
 * **Two fields for responsibility, and they do different jobs.** "Whose step"
 * is the one the page reasons with — it decides whether a late step reads as
 * waiting on the client. "Who exactly" is the line the client actually reads:
 * *Marjan — Fadi / Jehangir*, because a step addressed to a company is
 * addressed to nobody. Leave it blank and the generic label shows instead.
 *
 * **Stage groups the client page.** Pre-production, production, delivery —
 * the three headings a twenty-five step tracker needs to stay readable. A
 * project whose steps all land in one stage draws no headings at all.
 *
 * **Status is not here.** It changes on the row itself, where it can be
 * picked without opening anything — a status that costs two clicks and a form
 * is a status nobody keeps current.
 *
 * **A date can always be cleared.** Emptying the field writes null and the
 * client page says "to be scheduled". The gaps are the most useful thing on
 * the tracker, so making one has to be as easy as filling it.
 */
export function StepForm({
  initial,
  busy,
  submitLabel,
  onSubmit,
  onCancel
}: {
  initial: StepDraft;
  busy: boolean;
  submitLabel: string;
  onSubmit: (draft: StepDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<StepDraft>(initial);
  const set = (patch: Partial<StepDraft>) => setDraft(d => ({
    ...d,
    ...patch
  }));
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!draft.title.trim()) return;
    onSubmit(draft);
  }
  return <form onSubmit={submit} className="max-w-2xl" data-id="element-1512" data-mp="1dx4uie50bcgb">
      <label className="block" data-id="element-1513" data-mp="1itvbd3omyc2s">
        <span className="label block text-paper/40" data-id="element-1514" data-mp="1iuy0b62ee4g2">Step</span>
        <input value={draft.title} onChange={e => set({
        title: e.target.value
      })} placeholder="e.g. Ritz-Carlton Signature Villa" autoFocus className={FIELD} data-id="element-1515" data-mp="1nszhtmk6dvpf" />
      </label>

      <label className="mt-6 block" data-id="element-1516" data-mp="1xirgarveb8z6">
        <span className="label block text-paper/40" data-id="element-1517" data-mp="1xlnhcp2jidru">Note · optional</span>
        <input value={draft.note} onChange={e => set({
        note: e.target.value
      })} placeholder="e.g. Secure the villa and possible desert walk access." className={FIELD} data-id="element-1518" data-mp="21j30k3juj7a4" />
      </label>

      <div className="mt-6 grid gap-6 sm:grid-cols-2" data-id="element-1519" data-mp="c5y760oaqehk">
        <label className="block" data-id="element-1520" data-mp="c7j0xo2sdted">
          <span className="label block text-paper/40" data-id="element-1521" data-mp="h51a9fkpuamw">Stage</span>
          <select value={draft.phase} onChange={e => set({
          phase: e.target.value as StepPhase
        })} className={SELECT} data-id="element-1522" data-mp="h5kqmrupwqyt">
            {PHASE_PICKER.map(option => <option key={option.value} value={option.value} className={OPTION} data-id="element-1523" data-mp="4sqi338vts8z">
                {option.label}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-1524" data-mp="3r3kwup1wqjfq">
          <span className="label block text-paper/40" data-id="element-1525" data-mp="ske16yzwjz0p">Whose step</span>
          <select value={draft.owner} onChange={e => set({
          owner: e.target.value as StepOwner
        })} className={SELECT} data-id="element-1526" data-mp="sjmg4i3xljoo">
            {OWNER_PICKER.map(option => <option key={option.value} value={option.value} className={OPTION} data-id="element-1527" data-mp="io1b0b3kg3ap">
                {option.label}
              </option>)}
          </select>
        </label>
      </div>

      <label className="mt-6 block" data-id="element-1528" data-mp="33acl5984v3ss">
        <span className="label block text-paper/40" data-id="element-1529" data-mp="2ycuks786lwwe">Who exactly · shown to the client</span>
        <input value={draft.ownerName} onChange={e => set({
        ownerName: e.target.value
      })} placeholder="e.g. Marjan — Fadi / Jehangir" className={FIELD} data-id="element-1530" data-mp="r2fdx6ottorg" />
      </label>

      <div className="mt-6 grid gap-6 sm:grid-cols-2" data-id="element-1531" data-mp="h4rcjxwptj0b">
        <label className="block" data-id="element-1532" data-mp="h253tuy38xgi">
          <span className="label block text-paper/40" data-id="element-1533" data-mp="m29lhoaqfy8b">Date · leave blank if unknown</span>
          <input type="date" value={draft.start} onChange={e => set({
          start: e.target.value
        })} className={FIELD} data-id="element-1534" data-mp="19s3xk3n89iri" />
        </label>

        <label className="block" data-id="element-1535" data-mp="zupquvr3w8ce">
          <span className="label block text-paper/40" data-id="element-1536" data-mp="zto43u0q9xeu">Ends · only for a run of days</span>
          <input type="date" value={draft.end} onChange={e => set({
          end: e.target.value
        })} disabled={!draft.start} className={`${FIELD} disabled:opacity-30`} data-id="element-1537" data-mp="14siy7s45b8e9" />
        </label>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-1538" data-mp="27m69vcpp30zm">
        <button type="submit" disabled={busy || !draft.title.trim()} className={`${BUTTON_SOLID} disabled:opacity-40`} data-id="element-1539" data-mp="27klhntquru4x">
          {submitLabel}
        </button>
        <button type="button" onClick={onCancel} className={BUTTON_GHOST} data-id="element-1540" data-mp="2gmb7ww900n14">
          Cancel
        </button>
      </div>
    </form>;
}